import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { CmSettingsService, PortalCoreFeature } from '@ats/cm/core';
import { Unsubscribable } from '@ats/utility';
import { Observable, of } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { CmTableColumn } from '../../models';
import { CmTableService } from '../../services/table.service';

@Component({
    selector: 'cm-table-filter',
    templateUrl: './table-filter.component.html',
    styleUrls: ['./table-filter.component.scss'],
    standalone: false
})
export class CmTableFilterComponent extends Unsubscribable implements OnInit {
	@Input() field: string;
	column$: Observable<CmTableColumn>;
	showClear$: Observable<boolean>;
	form: UntypedFormGroup;
	toggleFilter: (field: string, value?: string[]) => void;
	readonly locFeature: PortalCoreFeature;

	get filter() {
		return this.form.get('filter');
	}

	constructor(private waiter: CmTableService, private fb: UntypedFormBuilder, private settings: CmSettingsService) {
		super();
		this.locFeature = this.settings.portalCoreFeature;
	}

	ngOnInit() {
		this.column$ = this.waiter.columns$.pipe(
			map(columns => columns.find(col => col.field === this.field)),
			switchMap(column => (column?.filter?.options ? of(column) : this.parseOptionsFromData$(column)))
		);

		this.form = this.fb.group({
			filter: null,
		});
		this.toggleFilter = this.waiter.toggleFilter;
		this.showClear$ = this.filter.valueChanges.pipe(map(val => !!val && !!val.length));

		this.column$.pipe(takeUntil(this.unsub)).subscribe(col => {
			if (col.filter?.options?.length) {
				this.filter.enable();
			} else {
				this.filter.disable();
			}
		});
	}

	clear() {
		this.filter.setValue(null);
		this.toggleFilter(this.field, []);
	}

	private parseOptionsFromData$(column: CmTableColumn): Observable<CmTableColumn> {
		return this.waiter.rows$.pipe(
			map(
				rows =>
					({
						...column,
						filter: {
							...column?.filter,
							options: rows
								.map(row => row[column.filterField])
								.reduce((acc, curr) => (acc.includes(curr) ? acc : [...acc, curr]), [])
								.sort(),
						},
					} as CmTableColumn)
			)
		);
	}
}
